import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { fmtDate } from "@/lib/meta";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Flame, Building2, UserRound, AlarmClock, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";

function Card({ title, icon: Icon, computedAt, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`insight-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-[#0047FF]" /><span className="font-heading font-bold text-gray-900">{title}</span></div>
        <span className="text-[11px] text-gray-400">{computedAt ? `as of ${fmtDate(computedAt)}` : "not computed yet"}</span>
      </div>
      {children}
    </div>
  );
}

export default function Intelligence() {
  const { user } = useAuth();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => { setLoading(true); api.get("/insights").then(({ data }) => setData(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const recompute = async () => {
    setBusy(true);
    try { await api.post("/insights/recompute"); toast.success("Recomputed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const g = (k) => data[k] || {};
  const rows = (k) => (data[k]?.data) || [];

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0047FF]" /></div>;

  return (
    <div>
      <PageHeader title="Market Intelligence" subtitle="Rule-based signals, refreshed nightly after the sheet sync.">
        {user?.role === "admin" && <Button size="sm" onClick={recompute} disabled={busy} data-testid="recompute-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Recompute now</Button>}
      </PageHeader>

      <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {g("weekly_digest").data?.text && (
          <div className="lg:col-span-2 bg-[#0047FF] text-white rounded-lg p-5" data-testid="insight-digest">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="h-4 w-4" /><span className="font-heading font-bold">Weekly Digest</span>
              <span className="text-[11px] text-white/60 ml-auto">as of {fmtDate(g("weekly_digest").computed_at)}</span></div>
            <p className="text-sm leading-relaxed text-white/90">{g("weekly_digest").data.text}</p>
          </div>
        )}

        <Card title="Hot Regions" icon={Flame} computedAt={g("hot_regions").computed_at}>
          {rows("hot_regions").length === 0 ? <Empty /> : (
            <div className="space-y-1.5">
              {rows("hot_regions").slice(0, 8).map((r) => (
                <div key={r.city} className="flex items-center justify-between text-sm">
                  <span className="font-medium">{r.city}</span>
                  <span className="text-xs text-gray-500">{r.leads} leads · {r.open} open · {r.won} won · <b className="text-gray-800">score {r.score}</b></span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Stalled Deals" icon={AlarmClock} computedAt={g("stalled_deals").computed_at}>
          {rows("stalled_deals").length === 0 ? <Empty msg="No stalled deals." /> : (
            <div className="space-y-1.5">
              {rows("stalled_deals").slice(0, 8).map((r) => (
                <div key={r.deal_id} className="flex items-center justify-between text-sm">
                  <span className="font-medium truncate">{r.property} <span className="text-gray-400">· {r.city}</span></span>
                  <span className="text-xs text-rose-600 font-semibold">{r.days_in_stage}d in {r.stage.replace(/_/g, " ")}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Consultant Reliability" icon={UserRound} computedAt={g("consultant_reliability").computed_at}>
          {rows("consultant_reliability").length === 0 ? <Empty /> : (
            <div className="space-y-1.5">
              {rows("consultant_reliability").slice(0, 8).map((r) => (
                <div key={r.consultant_id} className="flex items-center justify-between text-sm">
                  <span className="font-medium">{r.name}</span>
                  <span className="text-xs text-gray-500">{r.win_rate}% win · {r.won}/{r.closed} · {r.avg_cycle_days}d · <b className="text-gray-800">score {r.score}</b></span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Property Fit" icon={Building2} computedAt={g("property_fit").computed_at}>
          {rows("property_fit").length === 0 ? <Empty /> : (
            <div className="space-y-1.5">
              {rows("property_fit").slice(0, 8).map((r) => (
                <div key={r.property_id} className="flex items-center justify-between text-sm">
                  <span className="font-medium truncate">{r.name} <span className="text-gray-400">· {r.city}</span></span>
                  <span className="text-xs text-gray-500">{r.rooms || "?"} rooms · <b className="text-gray-800">fit {r.score}</b></span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Revisit Suggestions" icon={RotateCcw} computedAt={g("revisit_suggestions").computed_at}>
          {rows("revisit_suggestions").length === 0 ? <Empty msg="Nothing to revisit." /> : (
            <div className="space-y-1.5">
              {rows("revisit_suggestions").slice(0, 8).map((r) => (
                <div key={r.property_id} className="flex items-center justify-between text-sm">
                  <span className="font-medium truncate">{r.name} <span className="text-gray-400">· {r.city}</span></span>
                  <span className="text-xs text-gray-500">{r.status.replace(/_/g, " ")} · {r.prior_deals} prior</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Empty({ msg = "Not enough data yet." }) {
  return <div className="text-sm text-gray-400 py-4">{msg}</div>;
}
